import { useState, useEffect, type Dispatch, type SetStateAction } from "react";
import {
  defaultSiteSettings,
  getSiteSettings,
  saveSiteSettingsAPI,
} from "@/mocks/adminData";
import type { SiteSettings } from "@/mocks/adminData";
import { useImageUpload } from "@/hooks/useImageUpload";

// ---- Single Image Field ----
interface SingleImageFieldProps {
  label: string;
  hint?: string;
  value: string;
  onChange: (url: string) => void;
  token?: string;
}

function SingleImageField({ label, hint, value, onChange, token }: SingleImageFieldProps) {
  const [urlInput, setUrlInput] = useState("");
  const { trigger, el: uploadInput } = useImageUpload(
    (url) => { onChange(url); },
    token ? { uploadPath: "admin/settings/upload-image", token, fieldName: "image" } : {}
  );

  return (
    <div>
      {uploadInput}
      <label className="text-xs font-semibold text-gray-600 mb-2 block">{label}</label>
      {value && (
        <div className="mb-2 rounded-lg overflow-hidden border border-gray-100 bg-gray-50">
          <img src={value} alt={label} className="w-full max-h-32 object-cover"
            onError={e => { (e.target as HTMLImageElement).style.display = "none"; }} />
          <p className="text-xs font-medium text-gray-500 px-2 py-1 truncate">
            {value.startsWith("data:") ? "本地上传图片" : value}
          </p>
        </div>
      )}
      <div className="flex gap-2 mb-1">
        <button type="button" onClick={trigger}
          className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs text-gray-600 cursor-pointer transition-colors whitespace-nowrap flex-shrink-0">
          <i className="ri-upload-2-line"></i>上传图片
        </button>
        <input value={urlInput} onChange={e => setUrlInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter" && urlInput.trim()) {
              onChange(urlInput.trim());
              setUrlInput("");
            }
          }}
          className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400 min-w-0"
          placeholder="或粘贴图片链接（https://...）" />
        <button onClick={() => {
          if (urlInput.trim()) {
            onChange(urlInput.trim());
            setUrlInput("");
          }
        }}
          className="px-3 py-2 bg-red-500 text-white rounded-lg text-sm cursor-pointer hover:bg-red-600 transition-colors whitespace-nowrap flex-shrink-0">
          <i className="ri-check-line"></i>
        </button>
      </div>
      {hint && <p className="text-xs font-medium text-gray-500">{hint}</p>}
    </div>
  );
}

// ---- Settings Field ----
interface SettingsFieldProps {
  label: string;
  fieldKey: keyof SiteSettings;
  placeholder?: string;
  form: SiteSettings;
  setForm: Dispatch<SetStateAction<SiteSettings>>;
}

function SettingsField({ label, fieldKey, placeholder, form, setForm }: SettingsFieldProps) {
  return (
    <div>
      <label className="text-xs font-semibold text-gray-600 mb-1 block">{label}</label>
      <input
        value={form[fieldKey] as string}
        onChange={e => setForm(prev => ({ ...prev, [fieldKey]: e.target.value }))}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400 transition-colors"
        placeholder={placeholder || ""}
      />
    </div>
  );
}

// ---- Settings Tab ----
export default function SettingsTab({ token }: { token: string }) {
  const [form, setForm] = useState<SiteSettings>(defaultSiteSettings);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState("");

  const { trigger: triggerHeroImg, el: heroImgInput } = useImageUpload(
    (url) => { setForm(prev => ({ ...prev, hero_images: [...(prev.hero_images || []), url] })); },
    { uploadPath: "admin/settings/upload-image", token, fieldName: "image" }
  );
  const { trigger: triggerLogoImg, el: logoImgInput } = useImageUpload(
    (url) => { setForm(prev => ({ ...prev, logo_image: url })); },
    { uploadPath: "admin/settings/upload-image", token, fieldName: "image" }
  );

  useEffect(() => {
    setForm(getSiteSettings());
  }, []);

  const handleSave = async () => {
    setLoading(true);
    const success = await saveSiteSettingsAPI(form, token);
    setLoading(false);
    setMsg(success ? "保存成功，前台已同步更新" : "已保存到本地缓存（服务器连接失败）");
    setTimeout(() => setMsg(""), 3000);
  };

  const addHeroImage = () => {
    const url = newImageUrl.trim();
    if (!url) return;
    setForm(prev => ({ ...prev, hero_images: [...(prev.hero_images || []), url] }));
    setNewImageUrl("");
  };

  const removeHeroImage = (idx: number) => {
    setForm(prev => ({ ...prev, hero_images: prev.hero_images.filter((_, i) => i !== idx) }));
  };

  return (
    <div>
      {heroImgInput}{logoImgInput}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-bold text-gray-800">站点设置</h2>
      </div>

      {msg && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-xs text-green-600 flex items-center gap-2">
          <i className="ri-checkbox-circle-line"></i>{msg}
        </div>
      )}

      <div className="space-y-5">
        {/* 首页背景图 */}
        <div className="bg-white border border-gray-100 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-image-line text-red-400"></i>
            <h3 className="text-sm font-bold text-gray-700">首页背景图</h3>
            <span className="text-xs font-medium text-gray-500">（多张自动轮播）</span>
          </div>
          <div className="space-y-2 mb-3">
            {(form.hero_images || []).length === 0 && (
              <p className="text-xs font-medium text-gray-500 py-2">暂无图片，将使用默认背景</p>
            )}
            {(form.hero_images || []).map((url, idx) => (
              <div key={idx} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2">
                <div className="w-14 h-9 rounded overflow-hidden flex-shrink-0 bg-gray-200">
                  <img src={url} alt="" className="w-full h-full object-cover" onError={e => { (e.target as HTMLImageElement).style.display = "none"; }} />
                </div>
                <p className="flex-1 text-xs font-medium text-gray-600 truncate min-w-0">
                  {url.startsWith("data:") ? "本地上传图片" : url}
                </p>
                <button onClick={() => removeHeroImage(idx)}
                  className="w-6 h-6 flex items-center justify-center rounded hover:bg-red-50 cursor-pointer flex-shrink-0">
                  <i className="ri-delete-bin-line text-red-400 text-sm"></i>
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mb-2">
            <button onClick={triggerHeroImg}
              className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs text-gray-600 cursor-pointer transition-colors whitespace-nowrap flex-shrink-0">
              <i className="ri-upload-2-line"></i>上传图片
            </button>
            <input value={newImageUrl} onChange={e => setNewImageUrl(e.target.value)}
              onKeyDown={e => e.key === "Enter" && addHeroImage()}
              className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400 min-w-0"
              placeholder="或粘贴图片链接（https://...）" />
            <button onClick={addHeroImage}
              className="px-3 py-2 bg-red-500 text-white rounded-lg text-sm cursor-pointer hover:bg-red-600 transition-colors whitespace-nowrap flex-shrink-0">
              <i className="ri-add-line"></i>添加
            </button>
          </div>
          <p className="text-xs font-medium text-gray-500">支持上传本地图片或粘贴图片链接，多张图片前台自动轮播</p>
        </div>

        {/* 关于众兴 */}
        <div className="bg-white border border-gray-100 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-information-line text-red-400"></i>
            <h3 className="text-sm font-bold text-gray-700">关于众兴</h3>
            <span className="text-xs font-medium text-gray-500">（首页简介区域）</span>
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">区块标题</label>
              <input value={form.about_title || ""} onChange={e => setForm(prev => ({ ...prev, about_title: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400 transition-colors"
                placeholder="如：关于众兴" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">简介内容</label>
              <textarea value={form.about_text || ""} onChange={e => setForm(prev => ({ ...prev, about_text: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400 resize-none font-mono"
                rows={5} maxLength={1000} placeholder="输入关于众兴的简介文字..." />
              <p className="text-xs font-medium text-gray-500 mt-1">{(form.about_text || "").length}/1000</p>
              <div className="mt-2 p-2.5 bg-amber-50 border border-amber-100 rounded-lg space-y-1.5">
                <p className="text-xs font-semibold text-amber-700 flex items-center gap-1">
                  <i className="ri-palette-line"></i>文字样式设置方法
                </p>
                <p className="text-xs text-amber-600">· 用 <code className="bg-amber-100 px-1 rounded font-mono">**文字**</code> 包裹变红色加粗</p>
                <p className="text-xs text-amber-600">· 格式：<code className="bg-amber-100 px-1 rounded font-mono">[显示文字](链接地址)</code> 设置超链接</p>
              </div>
            </div>
          </div>
        </div>

        {/* 图片资源 */}
        <div className="bg-white border border-gray-100 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-4">
            <i className="ri-image-2-line text-red-400"></i>
            <h3 className="text-sm font-bold text-gray-700">页面图片资源</h3>
          </div>
          <div className="space-y-5">
            <SingleImageField
              label="核心价值观横幅图"
              hint="建议使用宽幅图片，将全宽展示"
              value={form.values_image || ""}
              onChange={url => setForm(prev => ({ ...prev, values_image: url }))}
              token={token}
            />
            <div className="border-t border-gray-100 pt-4">
              <SingleImageField
                label="寻求代投资源横幅图"
                hint="建议使用宽幅图片，将全宽展示"
                value={form.collab_image || ""}
                onChange={url => setForm(prev => ({ ...prev, collab_image: url }))}
                token={token}
              />
            </div>
          </div>
        </div>

        {/* 服务体系 */}
        <div className="bg-white border border-gray-100 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-customer-service-2-line text-red-400"></i>
            <h3 className="text-sm font-bold text-gray-700">服务体系</h3>
          </div>
          <div className="space-y-3">
            <SettingsField label="服务标题" fieldKey="service_title" placeholder="如：一对一专员" form={form} setForm={setForm} />
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">服务描述</label>
              <textarea value={form.service_desc || ""} onChange={e => setForm(prev => ({ ...prev, service_desc: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400 resize-none"
                rows={4} maxLength={500} placeholder="输入服务体系描述..." />
              <p className="text-xs font-medium text-gray-500 mt-1">{(form.service_desc || "").length}/500</p>
            </div>
            <SingleImageField
              label="服务配图"
              hint="右侧展示的配图，建议正方形图片"
              value={form.service_image || ""}
              onChange={url => setForm(prev => ({ ...prev, service_image: url }))}
              token={token}
            />
          </div>
        </div>

        {/* Logo */}
        <div className="bg-white border border-gray-100 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-image-edit-line text-red-400"></i>
            <h3 className="text-sm font-bold text-gray-700">Logo图片</h3>
            <span className="text-xs font-medium text-gray-500">（顶部导航 + 底部均使用）</span>
          </div>
          <div className="flex items-center gap-4 mb-3">
            <div className="w-14 h-14 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0 border border-gray-200">
              {form.logo_image ? (
                <img src={form.logo_image} alt="Logo" className="w-full h-full object-contain" onError={e => { (e.target as HTMLImageElement).style.display = "none"; }} />
              ) : (
                <i className="ri-image-line text-gray-300 text-2xl"></i>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-600 mb-2 truncate">{form.logo_image ? (form.logo_image.startsWith("data:") ? "本地上传图片" : form.logo_image) : "未设置，使用默认"}</p>
              <div className="flex gap-2">
                <button type="button" onClick={triggerLogoImg}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs text-gray-600 cursor-pointer transition-colors whitespace-nowrap">
                  <i className="ri-upload-2-line"></i>上传Logo
                </button>
                {form.logo_image && (
                  <button type="button" onClick={() => setForm(prev => ({ ...prev, logo_image: "" }))}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs text-red-400 hover:text-red-600 cursor-pointer whitespace-nowrap">
                    <i className="ri-delete-bin-line"></i>移除
                  </button>
                )}
              </div>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">或粘贴图片链接</label>
            <input value={form.logo_image && !form.logo_image.startsWith("data:") ? form.logo_image : ""}
              onChange={e => setForm(prev => ({ ...prev, logo_image: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400 min-w-0"
              placeholder="https://..." />
          </div>
          <p className="text-xs font-medium text-gray-500 mt-2">建议使用透明背景的PNG图片，尺寸 64×64 或更大</p>
        </div>

        {/* 商户信息 */}
        <div className="bg-white border border-gray-100 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-store-2-line text-red-400"></i>
            <h3 className="text-sm font-bold text-gray-700">商户信息</h3>
            <span className="text-xs font-medium text-gray-500">（显示在顶部导航）</span>
          </div>
          <div className="space-y-3">
            <SettingsField label="站点名称" fieldKey="site_name" placeholder="如：众兴 663.com" form={form} setForm={setForm} />
            <SettingsField label="商户名称" fieldKey="merchant_name" placeholder="如：迪猫" form={form} setForm={setForm} />
            <SettingsField label="商户TG账号" fieldKey="merchant_handle" placeholder="如：@hx_zs888" form={form} setForm={setForm} />
            <SettingsField label="商户TG链接" fieldKey="merchant_tg_url" placeholder="如：https://t.me/hx_zs888" form={form} setForm={setForm} />
          </div>
        </div>

        {/* 投诉接受 + Footer 合并 */}
        <div className="bg-white border border-gray-100 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-customer-service-2-line text-red-400"></i>
            <h3 className="text-sm font-bold text-gray-700">投诉接受</h3>
            <span className="text-xs font-medium text-gray-500">（底部投诉区域显示）</span>
          </div>
          <div className="space-y-3">
            <SettingsField label="投诉负责人名称" fieldKey="complaint_name" placeholder="如：迪猫" form={form} setForm={setForm} />
            <SettingsField label="投诉TG账号" fieldKey="complaint_handle" placeholder="如：@hx_zs888" form={form} setForm={setForm} />
            <SettingsField label="投诉TG链接" fieldKey="complaint_tg_url" placeholder="如：https://t.me/hx_zs888" form={form} setForm={setForm} />
          </div>
          <div className="border-t border-gray-100 mt-4 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <i className="ri-layout-bottom-line text-red-400"></i>
              <h3 className="text-sm font-bold text-gray-700">底部Footer</h3>
            </div>
            <div className="space-y-3">
              <SettingsField label="Footer TG账号" fieldKey="footer_tg" placeholder="如：@hx_zs888" form={form} setForm={setForm} />
              <SettingsField label="版权文字" fieldKey="footer_copyright" placeholder="如：众兴集团 © 2024  |  663.com" form={form} setForm={setForm} />
            </div>
          </div>
        </div>
      </div>

      <button onClick={handleSave} disabled={loading}
        className="w-full mt-5 py-3 bg-gradient-to-r from-red-500 to-orange-400 text-white rounded-xl text-sm font-semibold cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 whitespace-nowrap">
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <i className="ri-loader-4-line animate-spin"></i>保存中...
          </span>
        ) : (
          <span><i className="ri-save-line mr-1.5"></i>保存设置</span>
        )}
      </button>
    </div>
  );
}
